/**
 * Who asked for an execution — and, when it was an agent, exactly which text it
 * was permitted to run.
 *
 * ## Why this is a type and not a check
 *
 * Every MCP tool that makes the app run SQL classifies the tab or cell in the
 * main process first. Between that decision and the run there is a gap: another
 * MCP call can rewrite the text while the first is parked on an approval dialog
 * or an IPC round trip. The defence is to carry the classified text to the point
 * of execution and refuse if the live text is no longer one of them.
 *
 * Two rounds of that fix bound the paths that were pointed out and left their
 * siblings unbound — `ui_execute_query` on a notebook tab, and a run whose
 * classified list came back empty. Both were *representable* states: an optional
 * `expectedSource?: string` has a perfectly valid-looking `undefined`, and a
 * missing value reads as "no expectation" instead of "expect nothing".
 *
 * So the expectation is no longer optional anywhere. Every execution entry point
 * takes an `ExecutionAuthorization` as a **required** argument, and the agent
 * arm cannot be constructed without the list. A new execution path that forgets
 * to carry one does not compile, rather than running unchecked.
 *
 * ## The empty list means "nothing", not "anything"
 *
 * `{ origin: 'agent', allowedSources: [] }` authorises nothing at all — the
 * membership test fails for every string. That is deliberate: a guard that
 * classified zero units (a markdown cell, a cell that was not code when it was
 * checked) has permitted zero statements, and treating that as an absent
 * expectation is exactly the bug this type exists to remove.
 *
 * ## Membership, not equality
 *
 * A run-all classifies every code cell up front and then executes them one at a
 * time, so each cell is checked against the whole approved set rather than a
 * position in it. The guarantee that buys is the one worth stating: **nothing
 * executes whose text was not classified as permitted for this request.** It is
 * deliberately not "the notebook is unchanged" — an edit refuses that cell, and
 * an inserted cell is not in the runner's snapshot at all.
 */

export type ExecutionAuthorization =
  /** A person pressed Run. Their own editor buffer is self-authorising. */
  | { origin: 'user' }
  /** An agent asked. `allowedSources` is what the permission gate classified. */
  | { origin: 'agent'; allowedSources: readonly string[] };

/** The authorization every human-driven execution passes. */
export const USER_EXECUTION: ExecutionAuthorization = { origin: 'user' };

/**
 * Builds the agent arm. Takes the list positionally so an empty array is a
 * visible decision at the call site rather than an omitted argument.
 */
export const agentExecution = (allowedSources: readonly string[]): ExecutionAuthorization => ({
  origin: 'agent',
  allowedSources,
});

/** True when `liveSource` may be sent to the database under this authorization. */
export const isSourceAuthorized = (
  authorization: ExecutionAuthorization,
  liveSource: string
): boolean =>
  authorization.origin === 'user' || authorization.allowedSources.includes(liveSource);

/**
 * Shown when a refusal happens. Deliberately says what changed and that nothing
 * ran — a bare "permission denied" would read as a settings problem.
 */
export const UNAUTHORIZED_SOURCE_MESSAGE =
  'The text changed after the AI agent’s request was checked, so nothing was executed.';
